import { FeatureFlagKey, type FeatureFlagMap } from "encamp-shared/src/featureFlags";
import { Permission, type ScoutPackage } from "generated-graphql/graphql";
import { useAuthorization } from "hooks/useAuthorization";
import { useCurrentUser } from "hooks/useCurrentUser";
import { useFeatureFlags } from "hooks/useFeatureFlags";
import { type PropsWithChildren, type ReactElement } from "react";
import { Navigate } from "react-router-dom";

import { useServerScoutAccess } from "./hooks/useScoutAccess";

export const SCOUT_ACCESS_PACKAGES = {
  REGULATORY_ADVISOR: "regulatory-advisor",
  PRO: "pro",
} as const;

export type ScoutAccessPackage = (typeof SCOUT_ACCESS_PACKAGES)[keyof typeof SCOUT_ACCESS_PACKAGES];

export type ScoutAccessInput = {
  flags: Partial<FeatureFlagMap> | undefined;
  hasPermissions: (permissions: Permission[]) => boolean;
  isStaff?: boolean;
};

export function resolveScoutAccessPackage({
  flags,
  hasPermissions,
  isStaff = false,
}: ScoutAccessInput): ScoutAccessPackage | null {
  if (!flags?.[FeatureFlagKey.ScoutModule]) {
    return null;
  }

  if (isStaff) {
    return SCOUT_ACCESS_PACKAGES.PRO;
  }

  if (flags[FeatureFlagKey.ScoutProModule]) {
    const proIsScoped = Boolean(flags[FeatureFlagKey.ScoutProPermissionScoped]);
    if (!proIsScoped || hasPermissions([Permission.AccessScoutPro])) {
      return SCOUT_ACCESS_PACKAGES.PRO;
    }
  }

  const regulatoryAdvisorIsScoped = Boolean(
    flags[FeatureFlagKey.ScoutRegulatoryAdvisorPermissionScoped]
  );
  if (regulatoryAdvisorIsScoped && !hasPermissions([Permission.RouteScout])) {
    return null;
  }

  return SCOUT_ACCESS_PACKAGES.REGULATORY_ADVISOR;
}

export function hasScoutAccess(input: ScoutAccessInput): boolean {
  return resolveScoutAccessPackage(input) !== null;
}

const SCOUT_ACCESS_PACKAGE_BY_SERVER_PACKAGE = {
  PRO: SCOUT_ACCESS_PACKAGES.PRO,
  REGULATORY_ADVISOR: SCOUT_ACCESS_PACKAGES.REGULATORY_ADVISOR,
} as const satisfies Record<ScoutPackage, ScoutAccessPackage>;

export function useScoutAccessPackage(): {
  /** Gates Scout Pro features; Pro for a Regulatory Advisor user previewing Pro. */
  scoutAccessPackage: ScoutAccessPackage | null;
  /** The user's plan; label the user's tier with this, never with the preview. */
  scoutPlanPackage: ScoutAccessPackage | null;
  loading: boolean;
} {
  const { featureFlags, loading: featureFlagsLoading } = useFeatureFlags();
  const { hasPermissions, loading: authorizationLoading } = useAuthorization();
  const { isStaff, isEmulating, loading: currentUserLoading } = useCurrentUser();

  const planPackage = resolveScoutAccessPackage({
    flags: featureFlags,
    hasPermissions,
    isStaff: isStaff && !isEmulating,
  });
  // Only a Regulatory Advisor plan can resolve differently on the server (a
  // Scout Pro preview); the server owns that decision.
  const { scoutPackage: serverPackage, loading: serverLoading } = useServerScoutAccess(
    planPackage !== SCOUT_ACCESS_PACKAGES.REGULATORY_ADVISOR
  );

  return {
    scoutAccessPackage: serverPackage
      ? SCOUT_ACCESS_PACKAGE_BY_SERVER_PACKAGE[serverPackage]
      : planPackage,
    scoutPlanPackage: planPackage,
    loading: featureFlagsLoading || authorizationLoading || currentUserLoading || serverLoading,
  };
}

export function HasScoutAccess({
  fallback,
  children,
}: PropsWithChildren<{
  fallback?: string | ReactElement;
}>) {
  const { scoutAccessPackage, loading } = useScoutAccessPackage();

  if (loading) {
    return <></>;
  }

  if (scoutAccessPackage) {
    return <>{children}</>;
  }

  if (fallback && typeof fallback !== "string") {
    return fallback;
  }

  return <Navigate to={fallback ?? "../not-found"} replace />;
}
